import { useEffect, useRef, useState, type RefObject } from 'react';
import { toast } from 'sonner';
import { duringUnload, errorMessage } from './api';

/**
 * Feedback helpers for DESIGN.md rules 2 and 5: every action shows what happened, and reversible
 * actions offer Undo instead of an "Are you sure?" dialog.
 */

const UNDO_MS = 5_000;
/** Deletes get longer: noticing "that was the wrong one" takes a moment. */
export const UNDO_DELETE_MS = 8_000;

interface UndoableBase {
  /** Toast text, e.g. "Chat closed". */
  message: string;
  /** Second line in the toast: what the action does once it goes through. */
  description?: string;
  /** Update the UI immediately (optimistic). */
  apply: () => void;
  /** Put the UI back (Undo pressed, or the request failed). */
  revert: () => void;
}

/** For a change that only lives on this page so far (nothing sent): Undo just puts it back. */
export function undoToast(message: string, onUndo: () => void, description?: string): void {
  toast(message, { description, duration: UNDO_MS, action: { label: 'Undo', onClick: onUndo } });
}

/** Delayed actions still waiting for their undo window to pass. */
const pending = new Set<() => void>();

/** Send every delayed action now (logging out, or the page closing). */
export function flushPendingActions(): void {
  // Each fire() removes itself; deleting from a Set while iterating it is safe.
  for (const fire of pending) fire();
}

// Closing the tab mid-window must not silently drop a delete the user saw happen.
if (typeof window !== 'undefined')
  window.addEventListener('pagehide', () => duringUnload(flushPendingActions));

/**
 * Delay mode: the UI changes now, the request is only sent once the undo window passes, so Undo
 * is exact. Use it for deletes and for actions with side effects that can't be taken back
 * (closing a chat messages the citizen). If the request then fails, the UI is put back.
 */
export function undoableDelayed({
  message,
  description,
  apply,
  revert,
  commit,
  ms = UNDO_MS,
}: UndoableBase & { commit: () => Promise<unknown>; ms?: number }): void {
  apply();
  const fire = () => {
    pending.delete(fire);
    clearTimeout(timer);
    toast.dismiss(id);
    commit().catch((e: unknown) => {
      revert();
      toast.error(errorMessage(e));
    });
  };
  const timer = setTimeout(fire, ms);
  pending.add(fire);
  const id = toast(message, {
    description,
    duration: ms,
    action: {
      label: 'Undo',
      onClick: () => {
        pending.delete(fire);
        clearTimeout(timer);
        revert();
      },
    },
  });
}

/**
 * Inverse mode: the request is sent now; Undo sends the opposite request. Use it when the action
 * is cheap to reverse (toggling a KB entry active, ignoring an unanswered question).
 */
export function undoableNow({
  message,
  description,
  apply,
  revert,
  commit,
  inverse,
}: UndoableBase & { commit: () => Promise<unknown>; inverse: () => Promise<unknown> }): void {
  apply();
  commit()
    .then(() =>
      toast(message, {
        description,
        duration: UNDO_MS,
        action: {
          label: 'Undo',
          onClick: () => {
            revert();
            inverse().catch((e: unknown) => toast.error(errorMessage(e)));
          },
        },
      }),
    )
    .catch((e: unknown) => {
      revert();
      toast.error(errorMessage(e));
    });
}

/**
 * True for a moment after `success` becomes true: drives the button's "✓ Saved" state.
 * The rising edge is detected during render (React's "adjust state on prop change" pattern);
 * only the timed reset runs in an effect.
 */
export function useSuccessBeat(success: boolean, ms = 1200): boolean {
  const [prev, setPrev] = useState(success);
  const [beat, setBeat] = useState(false);
  if (success !== prev) {
    setPrev(success);
    if (success) setBeat(true);
  }
  useEffect(() => {
    if (!beat) return undefined;
    const t = setTimeout(() => setBeat(false), ms);
    return () => clearTimeout(t);
  }, [beat, ms]);
  return beat;
}

/** Restart a one-shot CSS animation class on an element. */
function replay(el: HTMLElement | null, cls: string): void {
  if (!el) return;
  el.classList.remove(cls);
  void el.offsetWidth; // force reflow so the animation restarts
  el.classList.add(cls);
  el.addEventListener('animationend', () => el.classList.remove(cls), { once: true });
}

/**
 * Brief highlight whenever `value` changes after mount (a live update changed it in place).
 * Returns a ref for the element to highlight.
 */
export function useFlash<E extends HTMLElement>(value: unknown): RefObject<E | null> {
  const ref = useRef<E>(null);
  const prev = useRef(value);
  useEffect(() => {
    if (Object.is(prev.current, value)) return;
    prev.current = value;
    replay(ref.current, 'animate-flash');
  }, [value]);
  return ref;
}

/** Shake an element when `trigger` becomes truthy or changes (invalid input), instead of a red banner. */
export function useShake<E extends HTMLElement>(trigger: unknown): RefObject<E | null> {
  const ref = useRef<E>(null);
  useEffect(() => {
    if (trigger) replay(ref.current, 'animate-shake');
  }, [trigger]);
  return ref;
}
